import * as React from "react";
import { connect } from "react-redux";
import { push } from "connected-react-router";
import styled from "styled-components";
import { Progress, Row, Col, Button, Input, Spin, Icon, message } from "antd";
import { RcFile } from "antd/lib/upload";
import { UploadFile } from "antd/lib/upload/interface";
import axios from "axios";
import Bluebird from "bluebird";

import {
  displayStringForExtraType,
  priceString,
  weekdayDayMonthDate,
  numberAsHour,
} from "../../../lib/formatters";
import { RootState } from "../../../store/rootReducer";
import { updateOrder, resetOrder, createOrderAsync } from "../actions";
import { MainComponentContainer } from "../../../shared/MainComponentContainer";
import RowWrapper from "../../../shared/RowWrapper";
import { SummaryEntry } from "./SummaryEntry";
import { SelectedExtraRow } from "./SelectedExtraRow";
import { CreateOrderRequest } from "../../../lib/model/CreateOrderRequest";
import { CreateAddressRequest } from "../../../lib/model/AddressState";
import { Order } from "../../../lib/model/Order";
import { ApiClient } from "../../../lib/api";
import DocumentUpload from "./DocumentUpload";
import { fetchOrdersAsync } from "../../orders/actions";
import TextArea from "antd/lib/input/TextArea";
import { renderAddress } from "../../../shared/addressRenderer";

const client = new ApiClient();

export const StyledInput = styled(Input)`
  height: 40px !important;
  background-color: #f2f2f2 !important;
  border: none !important;
  font-size: 11px !important;
  line-height: 15px !important;
  color: #386ee8 !important;
  font-weight: 300 !important;
  &::placeholder {
    color: #386ee8 !important;
    font-weight: 300 !important;
  }
`;

const StyledTextArea = styled(TextArea)`
  background-color: #f2f2f2 !important;
  border: none !important;
  font-size: 11px !important;
  line-height: 15px !important;
  color: #386ee8 !important;
  font-weight: 300 !important;
  padding: 12px !important;
  &::placeholder {
    color: #386ee8 !important;
    font-weight: 300 !important;
  }
`;

const Label = styled.span`
  color: #061a32;
  font-size: 11px;
  font-weight: 300;
  display: inline-block;
`;

const HintLabel = styled.span`
  font-size: 11px;
  font-weight: 300;
  display: inline-block;
  color: rgba(6, 26, 50, 0.7);
`;

const PriceLabel = styled.span`
  font-size: 28px;
  color: #386ee8;
`;

export const StyledProgress = styled(Progress)`
  position: fixed;
  top: calc(50% - 60px);
  left: calc(50% - 60px);

  .progress-label {
    position: absolute;
    font-size: 16px;
    line-height: 20px;
    top: 80px;
    width: 150px;
    left: -15px;
  }
`;

export const ProgressWrapper = styled.div`
  background-color: rgba(240, 240, 240, 0.7);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

export const formatProgressLabel = (percent: number | undefined) => (
  <>
    <div>{percent}%</div>
    <div className="progress-label">
      {(percent || 0) <= INITIAL_PERCENT
        ? "Auftrag wird erstellt"
        : "Dokumente werden hochgeladen"}
    </div>
  </>
);

type State = {
  isLoadingAddressCreate: boolean;
  pickupDocuments: RcFile[];
  dropoffDocuments: RcFile[];
  isUploading: boolean;
  uploadingProgress: number;
};

const mapStateToProps = (state: RootState) => ({
  order: state.bookDirect.order,
  quote: state.bookDirect.quote,
  isLoadingCreate: state.bookDirect.isLoadingCreate,
  createdOrder: state.bookDirect.createdOrder,
  addresses: state.addresses.addresses,
  openOrders: state.orders.openOrders,
  error: state.bookDirect.createError,
});

const dispatchProps = {
  push,
  updateOrder,
  resetOrder,
  createOrder: createOrderAsync.request,
  fetchOrders: fetchOrdersAsync.request,
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

export const PICKUP_DOCUMENT = "PICKUP_DOCUMENT";
export const DROPOFF_DOCUMENT = "DROPOFF_DOCUMENT";
export const INITIAL_PERCENT = 15;

class BookDirectSummaryContainer extends React.Component<Props, State> {
  readonly state: State = {
    isLoadingAddressCreate: false,
    pickupDocuments: [],
    dropoffDocuments: [],
    isUploading: false,
    uploadingProgress: INITIAL_PERCENT, // initial progress percent for generating signed urls
  };

  render() {
    const { order, quote, updateOrder, isLoadingCreate } = this.props;
    const {
      isLoadingAddressCreate,
      pickupDocuments,
      dropoffDocuments,
      isUploading,
      uploadingProgress,
    } = this.state;
    const isDocumentAvailable =
      pickupDocuments.length !== 0 || dropoffDocuments.length !== 0;

    return (
      <Spin
        size="large"
        indicator={<Icon type="loading" spin />}
        spinning={
          (isLoadingCreate || isLoadingAddressCreate) && !isDocumentAvailable
        }
        tip="Auftrag wird erstellt..."
      >
        <MainComponentContainer>
          <RowWrapper type="flex">
            <Col span={24}>
              <Row type="flex" gutter={21}>
                <Col
                  span={12}
                  xs={24}
                  sm={12}
                  md={12}
                  style={{ paddingBottom: "18px" }}
                >
                  <SummaryEntry
                    onEditClick={this.handleEditClicked}
                    title="Datum"
                    subTitle={!!order.date && weekdayDayMonthDate(order.date)}
                  ></SummaryEntry>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row type="flex" gutter={21}>
                <Col
                  span={12}
                  xs={24}
                  sm={12}
                  md={12}
                  style={{ paddingBottom: "18px" }}
                >
                  <SummaryEntry
                    onEditClick={this.handleEditClicked}
                    title="Beladung"
                    subTitle={`${numberAsHour(
                      order.startWindow[0]
                    )} bis ${numberAsHour(order.startWindow[1])}Uhr`}
                  >
                    <br />
                    {renderAddress(order.startAddress, this.props.addresses)}
                    <StyledInput
                      placeholder="Abholreferenz (optional)"
                      value={order.pickupComment}
                      onChange={(value) =>
                        updateOrder({
                          ...order,
                          pickupComment: value.currentTarget.value,
                        })
                      }
                    />
                    <DocumentUpload
                      handleBeforeUpload={(_, fileList: RcFile[]) =>
                        this.saveDocumentToLocal(PICKUP_DOCUMENT, fileList)
                      }
                      onRemove={(file: UploadFile) =>
                        this.deleteDocumentFromLocal(PICKUP_DOCUMENT, file)
                      }
                      fileList={pickupDocuments}
                      title="Abholdokumente"
                    />
                  </SummaryEntry>
                </Col>
                <Col
                  span={12}
                  xs={24}
                  sm={12}
                  md={12}
                  style={{ paddingBottom: "18px" }}
                >
                  <SummaryEntry
                    onEditClick={this.handleEditClicked}
                    title="Entladung"
                    subTitle={`${numberAsHour(
                      order.endWindow[0]
                    )} bis ${numberAsHour(order.endWindow[1])}Uhr`}
                  >
                    <br />
                    {renderAddress(order.endAddress, this.props.addresses)}
                    <StyledInput
                      placeholder="Anlieferreferenz (optional)"
                      value={order.dropoffComment}
                      onChange={(value) =>
                        updateOrder({
                          ...order,
                          dropoffComment: value.currentTarget.value,
                        })
                      }
                    />
                    <DocumentUpload
                      handleBeforeUpload={(_, fileList: RcFile[]) =>
                        this.saveDocumentToLocal(DROPOFF_DOCUMENT, fileList)
                      }
                      onRemove={(file: UploadFile) =>
                        this.deleteDocumentFromLocal(DROPOFF_DOCUMENT, file)
                      }
                      fileList={dropoffDocuments}
                      title="Anlieferdokumente"
                    />
                  </SummaryEntry>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row type="flex" gutter={21}>
                {order.loads.map((load, index) => (
                  <Col
                    key={`Summary${index}`}
                    span={12}
                    xs={24}
                    sm={12}
                    md={12}
                    style={{ paddingBottom: "18px" }}
                  >
                    <SummaryEntry
                      onEditClick={this.handleEditClicked}
                      title={
                        order.loads.length > 1
                          ? `Fracht ${index + 1}`
                          : "Fracht"
                      }
                    >
                      <Row>
                        <Col span={24}>
                          <Label
                            key={
                              load.label +
                              load.count +
                              load.totalWeight +
                              load.width
                            }
                          >
                            {load.count} x{" "}
                            {load.customLabel
                              ? `${load.customLabel} `
                              : "" + load.label}
                            <br />
                            {load.length} x {load.width} x {load.height}cm
                            <br />
                            {load.totalWeight}kg Gesamtgewicht
                          </Label>
                        </Col>
                      </Row>
                      <StyledInput
                        style={{ marginTop: "20px" }}
                        placeholder="Frachtreferenz (optional)"
                        value={load.internalReference}
                        onChange={(event) => {
                          const newLoads = [...order.loads];
                          newLoads[index] = {
                            ...load,
                            internalReference: event.target.value,
                          };
                          updateOrder({ ...order, loads: newLoads });
                        }}
                      />
                    </SummaryEntry>
                  </Col>
                ))}
                <Col
                  span={12}
                  xs={24}
                  sm={12}
                  md={12}
                  style={{ paddingBottom: "18px" }}
                >
                  <SummaryEntry
                    onEditClick={this.handleEditClicked}
                    title="Fahrzeugoptionen"
                  >
                    {order.extras.length > 0 ? (
                      order.extras.map((e, index) => (
                        <SelectedExtraRow
                          key={`ExtraRow${index}`}
                          title={displayStringForExtraType(e)}
                        />
                      ))
                    ) : (
                      <Label>keine</Label>
                    )}
                  </SummaryEntry>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row type="flex" gutter={21}>
                <Col span={12} xs={24} sm={12} md={12}>
                  <SummaryEntry title="Abrechnung und Kommentar">
                    <Row style={{ margin: "14px 0" }}>
                      <Col span={24}>
                        <StyledTextArea
                          placeholder="Abrechnungsreferenz (optional)"
                          rows={3}
                          value={order.internalReference}
                          onChange={(value) =>
                            updateOrder({
                              ...order,
                              internalReference: value.currentTarget.value,
                            })
                          }
                        />
                      </Col>
                      <HintLabel>
                        Diese Information erscheint auf Ihrer Rechnung.
                      </HintLabel>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <StyledTextArea
                          placeholder="Auftragskommentar (optional)"
                          rows={3}
                          value={order.comment}
                          onChange={(value) =>
                            updateOrder({
                              ...order,
                              comment: value.currentTarget.value,
                            })
                          }
                        />
                      </Col>
                    </Row>
                  </SummaryEntry>
                </Col>
                <Col span={12} xs={24} sm={12} md={12}>
                  <SummaryEntry title="Festpreis">
                    <Row type="flex" justify="space-between">
                      <Col span="auto">
                        <PriceLabel>
                          {quote ? priceString(quote.fullCost.net) : "- €"}
                        </PriceLabel>
                      </Col>
                      <Col span="auto">
                        <Button
                          type="primary"
                          disabled={true}
                          onClick={this.handleBookingClicked}
                          style={{
                            backgroundColor: "#386ee8 !important",
                            border: "none !important",
                            fontWeight: 300,
                            borderRadius: 0,
                            height: "36px",
                          }}
                        >
                          Jetzt kostenpflichtig buchen
                        </Button>
                      </Col>
                    </Row>
                    <Row type="flex">
                      <Col span={24} style={{ marginTop: 10 }}>
                        <HintLabel>
                          {`Inkl. ${
                            quote && quote.includedWaitingTime
                          } Minuten Wartezeit. Weitere Informationen in unseren `}
                          <a
                            href="https://forward-cargo.com/terms#waiting-times"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            AGB
                          </a>
                          . Unsere AGB und die ADSp 2017 finden Anwendung.
                        </HintLabel>
                      </Col>
                    </Row>
                  </SummaryEntry>
                </Col>
              </Row>
            </Col>
          </RowWrapper>
        </MainComponentContainer>
        {isUploading && isDocumentAvailable && (
          <ProgressWrapper>
            <StyledProgress
              type="circle"
              percent={uploadingProgress}
              format={formatProgressLabel}
              status="active"
              strokeColor="#386EE8"
            />
          </ProgressWrapper>
        )}
      </Spin>
    );
  }

  componentDidUpdate = (prevProps: Props, prevState: {}, snapshot: any) => {
    if (!prevProps.createdOrder && this.props.createdOrder) {
      // Start document upload after the order is created
      this.uploadDocuments(this.props.createdOrder);
    }
    if (!prevProps.error && this.props.error) {
      message.error(this.props.error.message);
    }
  };

  handleEditClicked = () => {
    this.props.push("/");
  };

  handleBookingClicked = async () => {
    this.setState({ isUploading: true, isLoadingAddressCreate: true });
    try {
      const createdAddressIds = await this.ensureAddressesCreated();

      const enhancedOrder = { ...this.props.order };
      if (createdAddressIds[0]) {
        enhancedOrder.startAddress = { id: createdAddressIds[0] };
      }
      if (createdAddressIds[1]) {
        enhancedOrder.endAddress = { id: createdAddressIds[1] };
      }

      this.props.createOrder(
        CreateOrderRequest.requestForDirectOrder(enhancedOrder)
      );
      this.setState({ isLoadingAddressCreate: false });
    } catch (error) {
      message.error(error.message);
    }
  };

  ensureAddressesCreated = async (): Promise<[string?, string?]> => {
    const returnValue: [string?, string?] = [undefined, undefined];
    if (this.props.order.startAddress.create) {
      const response = await client.createAddress(
        new CreateAddressRequest(this.props.order.startAddress.create)
      );
      returnValue[0] = response.id;
    }
    if (this.props.order.endAddress.create) {
      const response = await client.createAddress(
        new CreateAddressRequest(this.props.order.endAddress.create)
      );
      returnValue[1] = response.id;
    }
    return returnValue;
  };

  // upload file and calculate the progress
  uploadFile = (url: string, file: RcFile) => {
    const { pickupDocuments, dropoffDocuments, uploadingProgress } = this.state;
    const documentCounts = pickupDocuments.length + dropoffDocuments.length;
    const _this = this;

    return axios.put(url, file, {
      onUploadProgress: ({ total, loaded }) => {
        _this.setState({
          uploadingProgress:
            uploadingProgress +
            parseInt(
              Math.round(
                ((100 - INITIAL_PERCENT) / documentCounts) * (loaded / total)
              ).toFixed(2)
            ),
        });
      },
    });
  };

  // get the signed url for the document upload
  getSignedUrl = (orderId: string, fileExtension: string): Promise<string> => {
    return client
      .getStopDocumentUploadUrl(orderId, fileExtension)
      .then((r) => r.url);
  };

  // get the file extension from the upload file
  getFileExtension = (file: RcFile): string => {
    const fileName = file.name;
    return fileName.substr(fileName.lastIndexOf(".") + 1);
  };

  // Implement the document upload process. Can be improved later for the code optimization
  uploadDocuments = async (order: Order): Promise<void> => {
    const { pickupDocuments, dropoffDocuments } = this.state;
    const { id, shipmentIds } = order;
    const _this = this;
    let signedUrlsForPickupDocs: string[] = [],
      signedUrlsForDropoffDocs: string[] = [];

    if (pickupDocuments.length > 0) {
      // generate the signed urls for the pickup documents in order of precedence
      await Bluebird.mapSeries(pickupDocuments, function (document) {
        const fileExtension = _this.getFileExtension(document);
        return _this.getSignedUrl(id, fileExtension);
      }).then(function (urls) {
        signedUrlsForPickupDocs = urls;
      });

      // upload the pickup documents in order of precedence
      await Bluebird.mapSeries(pickupDocuments, function (doc, index) {
        return _this.uploadFile(signedUrlsForPickupDocs[index], doc);
      }).then(function () {
        console.log("PickupDocuments uploading is done");
      });
    }

    if (dropoffDocuments.length > 0) {
      // generate the signed urls for the dropoff documents in order of precedence
      await Bluebird.mapSeries(dropoffDocuments, function (document) {
        const fileExtension = _this.getFileExtension(document);
        return _this.getSignedUrl(id, fileExtension);
      }).then(function (urls) {
        signedUrlsForDropoffDocs = urls;
      });

      // upload the dropoff documents in order of precedence
      await Bluebird.mapSeries(dropoffDocuments, function (doc, index) {
        return _this.uploadFile(signedUrlsForDropoffDocs[index], doc);
      }).then(function () {
        console.log("DropoffDocuments uploading is done");
      });
    }

    // parse the signed urls to exclude the query params
    const parsedPickupDocLinks = signedUrlsForPickupDocs.map(
      (url) => url.split("?")[0]
    );
    const parsedDropoffDocLinks = signedUrlsForDropoffDocs.map(
      (url) => url.split("?")[0]
    );

    // upload the documents if the documents are uploaded in the local
    if (pickupDocuments.length !== 0 || dropoffDocuments.length !== 0) {
      await client.updateDocuments(
        shipmentIds ? shipmentIds[0] : "",
        parsedPickupDocLinks,
        parsedDropoffDocLinks
      );
    }

    this.setState({ isUploading: false });

    // order creation successfull

    message.success("Auftrag erfolgreich erstellt.");

    this.props.push("/orders");
    this.props.resetOrder();
  };

  saveDocumentToLocal = (documentType: string, fileList: RcFile[]) => {
    const { pickupDocuments, dropoffDocuments } = this.state;
    if (documentType === PICKUP_DOCUMENT) {
      this.setState({ pickupDocuments: [...pickupDocuments, ...fileList] });
    } else {
      this.setState({ dropoffDocuments: [...dropoffDocuments, ...fileList] });
    }

    return false;
  };

  deleteDocumentFromLocal = (documentType: string, file: UploadFile) => {
    const { pickupDocuments, dropoffDocuments } = this.state;

    if (documentType === PICKUP_DOCUMENT) {
      const newFileList = pickupDocuments.filter(
        (document) => document.uid !== file.uid
      );
      this.setState({ pickupDocuments: newFileList });
    } else {
      const newFileList = dropoffDocuments.filter(
        (document) => document.uid !== file.uid
      );
      this.setState({ dropoffDocuments: newFileList });
    }
  };
}

export default connect(
  mapStateToProps,
  dispatchProps
)(BookDirectSummaryContainer);
